import { ABTestAssignments } from '../api/ReplicantAPI';
import DB, { Entry, hasMigratedLegacyUserSharedStateItems } from '../db/DB';
import { ReplicantError, ReplicantErrorCode } from '../Errors';
import logger from '../logger';
import { Replicant, ReplicantConfig } from '../ReplicantConfig';
import SB from '../SchemaBuilder';
import { isAnonymousWebPlayer } from '../utils/isAnonymousWebPlayer';
import { createVirtualEntry, VirtualEntry } from '../utils/VirtualState';
import type { APIAccessInterface, WriteChangesetFN } from './ServerReplicant';
import { platformEnforcesChatbotQuotas } from './ServerReplicantChatbot';
import type ServerReplicantSharedStates from './ServerReplicantSharedStates';
import { updateSnapshotTimestamps } from './StateRecovery';
import { updateABTestAssignments } from './updateABTestAssignments';

type GetABTestAssignmentsFn = (opts: {
    entry?: { metainfo: { abTestAssignments?: ABTestAssignments }; state: object };
    isNewUser: boolean;
    userId: string;
}) => Promise<ABTestAssignments | undefined>;

const platformStorageSchema = SB.object({
    entry: SB.object({
        count: SB.int(),
    }).additionalProperties(),
}).additionalProperties();

export default class ServerReplicantCreateOrLogin<T extends Replicant> {
    private config: ReplicantConfig<T>;
    private db: DB<T['state'], T['messages']>;

    constructor(
        private opts: {
            apiAccess: APIAccessInterface<T>;
            db: DB<T['state'], T['messages']>;
            migrateUserSharedStates: ServerReplicantSharedStates['migrateUserSharedStates'];
            writeChangeset: WriteChangesetFN;
        },
    ) {
        this.config = opts.apiAccess.config;
        this.db = opts.db;
    }

    async createOrLogin(opts: {
        clientAppName?: string;
        convertFromWebPlayer?: { token: string; userId: string };
        getAbTestAssignments?: GetABTestAssignmentsFn;
        id: string;
        sessionId?: string;
        sessionName?: string;
        entry: Entry<T['state']> | null;
    }): Promise<Entry<T['state']>> {
        const { clientAppName, convertFromWebPlayer, entry, getAbTestAssignments, id, sessionId, sessionName } = opts;

        const newEntry = entry
            ? await this.login({
                  convertFromWebPlayer,
                  id,
                  getAbTestAssignments,
                  sessionId,
                  sessionName,
                  entry,
              })
            : await this.createUser({
                  clientAppName,
                  convertFromWebPlayer,
                  getAbTestAssignments,
                  id,
                  sessionId,
                  sessionName,
              });

        if (convertFromWebPlayer) {
            await Promise.all([
                // Remove consumed login token:
                this.db.loginTokens.deleteLoginToken(convertFromWebPlayer),

                // Mark web player as converted to platform user:
                this.db.chatbot.set(convertFromWebPlayer.userId, (chatbotMetainfo) => {
                    chatbotMetainfo.convertedToUserId = id;
                }),
            ]).catch(async (error) => {
                // Do not block entry on cleanup errors.
                const warning = new ReplicantError(
                    'Web player conversion cleanup failed',
                    'server_error',
                    undefined,
                    'warning',
                    { cause: error },
                );

                await this.opts.apiAccess.reportError(warning);
            });
        }

        return newEntry;
    }

    private async login(opts: {
        convertFromWebPlayer?: { token: string; userId: string };
        getAbTestAssignments?: GetABTestAssignmentsFn;
        id: string;
        sessionId?: string;
        sessionName?: string;
        entry: Entry<T['state']>;
    }): Promise<Entry<T['state']>> {
        const { convertFromWebPlayer, getAbTestAssignments, id, sessionId, sessionName, entry: originalEntry } = opts;

        // Migrate to the latest version if necessary.
        const latestVersion = this.config.migrator.getLatestVersion();

        // We've already played on a newer version. Playing here could unleash hell.
        if (originalEntry.version > latestVersion) {
            throw new ReplicantError(
                'Unknown schema version: ' + originalEntry.version + ', latest is: ' + latestVersion,
                ReplicantErrorCode.version_error,
            );
        }

        let virtual: VirtualEntry<T['state']>;

        try {
            virtual = createVirtualEntry(this.config, originalEntry);
        } catch (validationError: any) {
            // If the state schema does not validate AND we are in development mode AND there are no migrations set up,
            // assume that we're still in initial development, where the schema changes a lot, so nuke the user and
            // return a new one.
            if (this.opts.apiAccess.isInitialDevelopment()) {
                logger.info(
                    'Validation error on state: ' +
                        validationError +
                        '\nSince we are running in initial development mode, nuke and re-create the user.',
                );

                await this.db.delete(id);
                return this.createUser({ id, sessionId });
            }

            throw new ReplicantError(validationError, 'replication_error', 'user_login_error');
        }

        const entry = virtual.entry;

        if (convertFromWebPlayer) {
            await this.tryConvertFromWebPlayer({
                platformUser: { entry, userId: id },
                webPlayer: convertFromWebPlayer,
            });
        }

        const isNewUser = !convertFromWebPlayer && entry.lastLoginAt === undefined; // If user was created in a webhook but has never logged in, consider them a new user

        const abTestAssignments = await getAbTestAssignments?.({ isNewUser, userId: id, entry });

        if (abTestAssignments) {
            updateABTestAssignments(entry, {
                newAssignments: abTestAssignments,
                isNewUser,
            });
        }

        const now = this.opts.apiAccess.dateNow();
        entry.lastLoginAt = now;

        const isClientRefresh = sessionId && sessionId === entry.metainfo.lastSessionId;

        if (!isClientRefresh) {
            incrementLoginCount(entry, this.config);
        }

        // Don't mutate entry.state or entry.metainfo after taking a snapshot
        const snapshot = virtual.snapshot();

        snapshot.entry.rev++;
        snapshot.entry.metainfo.lastSessionId = sessionId;
        snapshot.entry.metainfo.lastSessionName = sessionName;

        const snapshotUpdate = updateSnapshotTimestamps({ entry: snapshot.entry, now });

        // TODO: remove after completing user shared state items migration
        if (!hasMigratedLegacyUserSharedStateItems(entry)) {
            const userSharedStateItems = Object.values(entry.userSharedStates).flatMap((items) => Object.values(items));

            await this.db.sharedStates.migrateLegacyUserSharedStates(userSharedStateItems);
        }

        await this.db.writeEntryOrEntryDiff({
            oldEntryDiffs: [],
            recoverySnapshotUpdate: snapshotUpdate,
            snapshot,
            userId: id,
        });

        const writeMigratedUserSharedStatesToDb = this.opts.migrateUserSharedStates(entry.userSharedStates);

        // Perform additional writes after successfully writing entry:
        await Promise.all([
            this.opts.writeChangeset({
                id,
                entry: snapshot.entry,
                replication: { queue: [], results: [] },
                messages: [],
                diff: snapshot.diff,
                snapshotUpdate,
            }),

            // Write user shared state migrations after migrating user:
            writeMigratedUserSharedStatesToDb?.(),

            // Reset chatbot metainfo session on login:
            platformEnforcesChatbotQuotas(this.opts.apiAccess.platform) && entry.chatbotMetainfo?.messengerId
                ? this.db.chatbot.resetMessagingSession(id, now).then((newChatbotMetainfo) => {
                      entry.chatbotMetainfo = newChatbotMetainfo;
                  })
                : undefined,
        ]);

        entry.rev = snapshot.entry.rev;
        entry.mainItemRev = snapshot.entry.mainItemRev;
        entry.metainfo = snapshot.entry.metainfo;

        return entry;
    }

    async createUser(opts: {
        clientAppName?: string;
        convertFromWebPlayer?: { token: string; userId: string };
        getAbTestAssignments?: GetABTestAssignmentsFn;
        id: string;
        sessionId?: string;
        sessionName?: string;
    }): Promise<Entry<T['state']>> {
        const { clientAppName, convertFromWebPlayer, getAbTestAssignments, id, sessionId, sessionName } = opts;

        // Create new user and write to db.
        const initialState = this.config.stateSchema.getDefault()!;

        const now = this.opts.apiAccess.dateNow();

        // Users may be created outside of login, for example in response to a chatbot webhook or as an entry read fallback; in these cases session ID is undefined:
        const isLogin = !!sessionId;

        const entry: Entry<T['state']> = {
            state: initialState,
            version: this.config.migrator.getLatestVersion(),
            rev: 0,
            mainItemRev: 0,
            metainfo: {
                clientAppName,
                lastSessionName: sessionName,
                loginCount: isLogin ? 1 : 0,
                random: { n: 0 },
            },
            metainfoMVCC: {},
            lastUpdated: now,
            createdAt: now,
            userSharedStates: {},
        };

        if (convertFromWebPlayer) {
            await this.tryConvertFromWebPlayer({
                platformUser: { entry, userId: id },
                webPlayer: convertFromWebPlayer,
            });
        }

        if (isLogin) {
            entry.lastLoginAt = now;
        }

        const isNewUser = !convertFromWebPlayer;

        const abTestAssignments = await getAbTestAssignments?.({ isNewUser, userId: id, entry });

        if (abTestAssignments) {
            updateABTestAssignments(entry, {
                newAssignments: abTestAssignments,
                isNewUser,
            });

            // This helps us differentiate between users that have logged in themselves,
            // and users that have had someone else log in for them.
            entry.metainfo.lastSessionId = sessionId;
        }

        await this.db.write(id, entry, { noOverwrite: true });

        return entry;
    }

    private async tryConvertFromWebPlayer(opts: {
        platformUser: { userId: string; entry: Entry<T['state']> };
        webPlayer: { token: string; userId: string };
    }): Promise<void> {
        const { platformUser, webPlayer } = opts;

        if (isAnonymousWebPlayer(platformUser.userId)) {
            throw Error('Cannot convert web player to another web player');
        }

        if (!isAnonymousWebPlayer(webPlayer.userId)) {
            throw Error('Cannot convert non-web player to platform user');
        }

        const { convertedFromUserId } = platformUser.entry.metainfo;

        if (convertedFromUserId) {
            if (convertedFromUserId === webPlayer.userId) {
                return; // Platform user is already converted from this web player: resume login
            } else {
                throw Error('Platform user is already converted from another web player');
            }
        }

        platformUser.entry.metainfo.convertedFromUserId = webPlayer.userId;

        if (platformUser.entry.lastLoginAt) {
            throw Error('Cannot convert to platform user who has already logged in');
        }

        const loginToken = await this.opts.db.loginTokens.fetchLoginToken(webPlayer);

        if (!loginToken) {
            throw Error('Login token not found');
        }

        if (loginToken.expiresAtMs <= this.opts.apiAccess.dateNow()) {
            throw Error('Login token is expired');
        }

        const webPlayerResult = await this.opts.apiAccess.getReadOnlyEntry(webPlayer.userId);

        if (!webPlayerResult) {
            throw Error('Web player not found');
        }

        const { convertedToUserId } = webPlayerResult.entry.chatbotMetainfo ?? {};

        if (convertedToUserId && convertedToUserId !== platformUser.userId) {
            throw Error('Web player is already converted to another platform user');
        }

        Object.assign(platformUser.entry.state, webPlayerResult.entry.state);

        Object.assign(platformUser.entry.metainfo, webPlayerResult.entry.metainfo);

        if (Object.keys(webPlayerResult.entry.chatbotMetainfo ?? {}).length > 0) {
            await this.db.chatbot.set(platformUser.userId, (chatbotMetainfo) => {
                Object.assign(chatbotMetainfo, webPlayerResult.entry.chatbotMetainfo);
            });
        }
    }
}

function incrementLoginCount(entry: Entry<any>, config: ReplicantConfig<any>): void {
    // Handle users created before `loginCount` was introduced:
    if (entry.metainfo.loginCount === undefined) {
        entry.metainfo.loginCount = 0;

        // Try to migrate `loginCount` from GCInstant's platform storage:
        try {
            const platformStorage: unknown = config.getPlatformStorage
                ? config.getPlatformStorage(entry.state)
                : entry.state.gcinstant?.platformStorage; // Default platform storage path

            if (platformStorageSchema.isValid(platformStorage)) {
                entry.metainfo.loginCount = platformStorage.entry.count;
            }
        } catch {}
    }

    entry.metainfo.loginCount += 1;
}
